import hyperdom from 'hyperdom'
import normalisePromotionCode from '../../../../packages/price-engine/normalisePromotionCode.js'
import {Message, MessageTypes} from '../message.jsx'
import formatDate from '../../../../packages/dates/formatDate.js'
import earliestPromoPaymentDate from '../../../../packages/promotions/earliestPromoPaymentDate.js'
import LocalDate from '@booking-services/localDate'
import formats from '../../../../packages/dates/formats.js'

class DiscountCode {
  constructor (options) {
    this.bookingCreated = options.bookingCreated
    this.clearErrors = options.clearErrors
    this.config = options.config
    this.criteriaLoaded = options.progress.criteriaLoaded
    this.departure = options.departure
    this.features = options.config.features
    this.hideLoading = options.hideLoading
    this.modals = options.modals
    this.progress = options.progress
    this.promotionCode = ''
    this.promotionError = options.promotionError || ''
    this.promotions = options.promotions
    this.quoteCriteria = options.quoteCriteria
    this.referralPromoEnabled = options.config.features.referrals.referralPromoEnabled
    this.referrals = options.config.features.referrals.referrals
    this.refferal = options.quoteCriteria.referral
    this.shouldRenderRefferalTooltip = false
    this.showLoading = options.showLoading
    this.showProspectivePromotionNameOnly = options.config.features.showProspectivePromotionNameOnly
    this.api = options.api
    this.user = options.user
    this.quoteCriteria.promotionAddedSuccessfuly = false
  }

  clearPromoErrors () {
    if(this.quoteCriteria) {
      delete this.quoteCriteria.promotionInvalidDueToAutomaticPromotion
      delete this.quoteCriteria.promotionInvalidDueToCombinabilityRules
    }
    delete this.promotionError
    delete this.promotionsLimitReached
  }

  async addPromotionByUserSuppliedCode () {
    this.showLoading()
    this.promotionCodeNotAdded = false

    try {
      const response = await this.loadPromotion(this.promotionCode, true)
      this.hideLoading()
      if (!this.showDiscountNotes()) {
        this.clearProspectivePromotion()
      }
      return response
    } catch(ex) {
      this.hideLoading()
      console.error(ex)
    }
  }

  async addReferralOrPromoCode () {
    const referralCode = this.promotionCode && this.referralPromoEnabled && this.promotionCode.trim()
    if (referralCode && referralCode.toLowerCase().startsWith('ref-')) {
      await this.addReferralByUserSuppliedCode(referralCode)
    } else {
      await this.addPromotionByUserSuppliedCode()
    }
  }

  async loadReferral (referralCode) {
    if (referralCode) {
      const referral = await this.api.referral(this.config.sellingCompanyCode, this.departure.productCode, this.departure.code, referralCode)
      return referral
    }
  }

  async addReferralByUserSuppliedCode (referralCode) {
    this.quoteCriteria.referral.message = ''
    if(!this.canAddReferral()) {
      return
    }
    if (!referralCode.match(/^\S*$/)) {
      this.quoteCriteria.referral.message = "Referral code does not exist"
      return
    }
    this.showLoading()
    delete this.promotionError
    const referral = await this.loadReferral(referralCode)
    if(referral.success) {
      this.quoteCriteria.referral.promotion = referral.promotion
      this.quoteCriteria.referral.referralCode = referral.referralCode
      this.quoteCriteria.referral.message = ""
      this.addAvailablePromotion(referral.promotion)
      this.clearProspectivePromotion()
    } else {
      this.quoteCriteria.referral.message = referral.message
    }
    this.hideLoading()
  }

  loadPromotion (promotionCode, showSuccessMessage) {
    this.clearPromoErrors()
    if (promotionCode) {
      return this.api.promotion(this.config.sellingCompanyCode, this.departure.productCode, this.departure.code, normalisePromotionCode(promotionCode))
        .then(promotion => {
          if(!['ADV', 'REF'].includes(promotion.promotionSubtype)) {
            delete this.promotionError
            this.addAvailablePromotion(promotion, showSuccessMessage)
          } else {
            this.promotionError = `invalid promotion subtype`
          }
        })
        .catch(error => {
          this.promotionError = error
          this.clearErrors()
        })
    } else {
      return Promise.resolve()
    }
  }

  userMustAcceptNotes (promotion) {
    return promotion.promotionSubtype === 'LMD'
      ? promotion.notes && this.user.isTravelAgent
      : promotion.notes
  }

  addAvailablePromotion (promotion, showSuccessMessage) {
    this.promotionsLimitReached = this.config.features.restrictPromotions && this.quoteCriteria.promotions.length > 1
    if (this.promotionsLimitReached){
      return
    }
    if (this.userMustAcceptNotes(promotion)) {
      this.modals.show({
        render: () => this.renderDiscountNotes({
          promotion,
          close: () => this.modals.close(),
        }),
      })
    } else {
      if(promotion.lmd) {
        delete this.quoteCriteria.lmdRemoved
      }
      this.promotions.addPromotion(promotion, showSuccessMessage)
    }
  }

  promoWarningMessage () {
    let message = ''
    if (this.promotionError) {
      message = "This code is either not applicable for this departure, expired or invalid"
    } else if (this.quoteCriteria.referral.message) {
      message = this.quoteCriteria.referral.message
    } else {
      message = `The promotion code you have entered is not combinable, Please contact the call centre on ${this.config.phone.general}`
    }
    this.clearPromoErrors()
    return message
  }

  renderReferralTooltip () {
    return this.referralPromoEnabled
      ? <div className="tooltip-referral" onclick={() => this.shouldRenderRefferalTooltip = true}>
        {
          this.shouldRenderRefferalTooltip ?
            <div className="tooltip-referral__content" onclick={ev => ev.stopPropagation()}>
              <div className="tooltip-referral__heading">
                <div className="tooltip-referral__header">Referral Program</div>
                <div className="tooltip-referral__close" onclick={() => this.shouldRenderRefferalTooltip = false}></div>
              </div>

              <div className="tooltip-referral__description">
                {hyperdom.rawHtml('', this.features.referrals.toolTipText)}
              </div>
            </div> : undefined
        }
      </div>
      : undefined
  }

  showDiscountNotes () {
    return this.prospectivePromotion && (
      this.prospectivePromotion.notes ||
      this.prospectivePromotion.paymentBy ||
      this.prospectivePromotion.depositPaymentBy
    )
  }

  promotionsMayBeAdded () {
    return this.progress.criteriaLoaded &&
      !this.bookingCreated()
  }

  renderDiscountBox ({modal}) {
    if (!this.promotionsMayBeAdded()) { return }

    return <form class={{'discount-code__form-modal' : modal}} onsubmit={ev => {
      ev.preventDefault()
      return this.addReferralOrPromoCode()
    }}>
      {
        this.promotionsLimitReached
          ? <div className='discount-code'>
            {new Message({
              message: `You’re a true deal chaser and have hit your 2 promo code max. Have more than 2 promo codes? Please feel free to call our reservations team for assistance at ${this.config.phone.general}`,
              type: MessageTypes.WARNING,
              hideIcon: true,
            })}
          </div>
          : undefined
      }
      {
        this.quoteCriteria.promotionAddedSuccessfuly
          ? <div className='discount-code'>
            {(() => {
              return new Message({
                message: 'Promo Code applied successfully.',
                type: MessageTypes.SUCCESS,
                hideIcon: true,
                onClose: () => this.quoteCriteria.promotionAddedSuccessfuly = false,
              })
            })()}
          </div>
          : undefined
      }

      {
        this.promotionError || this.quoteCriteria.promotionInvalidDueToAutomaticPromotion || this.refferal.message
          ? <div className='discount-code'>
            {(() => {
              const warningText = this.promoWarningMessage()
              return new Message({
                message: warningText,
                type: MessageTypes.WARNING,
                hideIcon: true,
              })
            })()}
          </div>
          : undefined
      }
      <div className='discount-code'>
        <div className='discount-code__form'>
          <div className='discount-code__box'>
            <div className='discount-code__header'>
              <p className='discount-code-info'>{this.referralPromoEnabled ? 'Add discount or referral code' : 'Add discount code'}</p>
              {this.renderReferralTooltip()}
            </div>
            <span>
              <input
                type='text'
                className='discount-code__field'
                binding={[this, 'promotionCode']}
                placeholder={this.referralPromoEnabled ? 'Promo or referral code' : 'Discount code'}
                required>
              </input>
              <button className='btn-secondary-outline'>Apply</button>
            </span>
          </div>
        </div>
      </div>
    </form>
  }

  clearProspectivePromotion () {
    delete this.promotionCode
  }

  canAddReferral () {
    const validationResult = this.quoteCriteria.passengers.map(passenger => {
      const errors = []
      if(passenger?.pastGuestBenefit?.selected || passenger?.frequentTravellerDiscount?.selected) {
        errors.push('pastTraveler')
      }
      if(passenger.applyAdvocatePromo) {
        errors.push('advocatePromo')
      }
      return errors
    })
    const isValid = validationResult.filter(item => item.length > 0).length !== this.quoteCriteria.passengers.length
    if(!isValid) {
      const validationReasons = validationResult.flat()
      const hasPastTraveller = validationReasons.some(item => item === 'pastTraveler')
      const advocatePromo = validationReasons.some(item => item === 'advocatePromo')
      const errors = []
      if(hasPastTraveller) {
        errors.push('past traveler savings')
      }
      if(advocatePromo) {
        errors.push('referral advocate savings')
      }
      this.quoteCriteria.referral.message = `Referral savings are not combinable with ${errors.join(' or ')}.`
    }
    return isValid
  }

  acceptPromotionNotes (promotion) {
    this.promotions.addPromotion(promotion)
    this.clearProspectivePromotion()
  }

  renderDiscountNotes ({promotion, close}) {
    const header = this.showProspectivePromotionNameOnly
      ? promotion.name
      : `${promotion.code} - ${promotion.name}`

    return <div class='promo-popup'>
      <div class="promo-popup__content">
        <div class="promo-popup__header">{header}</div>
        { promotion.notes
          ? <div class='promo-popup__text'>{promotion.notes}</div>
          : undefined
        }
        { promotion.paymentBy || promotion.depositPaymentBy
          ? <div class='promo-popup__text'>{this.discountInfoMessages(promotion)}</div>
          : undefined
        }
        <div class="promo-popup__buttons">
          <button class={`btn-promo`} href='#' onclick={(ev) => {ev.preventDefault();  close(); this.clearProspectivePromotion()}}>No, thank you</button>
          <button class={`btn-promo reversed-colors`} onclick={(ev) => {ev.preventDefault(); close(); this.acceptPromotionNotes(promotion)}} type='button'>Yes, I agree</button>
        </div>
      </div>
    </div>
  }

  discountInfoMessages (discount) {
    const discountMessage = paymentBy => <span className='date'>{
      formatDate(
        earliestPromoPaymentDate(paymentBy, this.departure, new LocalDate()),
        formats.humanDayMonthYear
      )
    }</span>

    let messages = []
    if (discount.depositPaymentBy) {
      messages.push('Pay deposit by ', discountMessage(discount.depositPaymentBy))
    }
    if (discount.paymentBy) {
      messages.push(
        messages.length ? ' and ' : 'Pay ',
        'in full by ',
        discountMessage(discount.paymentBy)
      )
    }
    return messages.length ? messages : undefined
  }
}

export default DiscountCode
